import Vue from 'vue';
import Tooltip from 'tooltip.js';
import dayjs from 'dayjs';
import { filesize } from 'src/utils/files';
import { mapGetters } from 'vuex';
import ObjectBrowser from './ObjectBrowser.vue';
import SurveyList from './SurveyList.vue';
import Modal from 'src/components/widgets/Modal.vue';
import Progressbar from 'src/components/widgets/Progressbar.vue';
import Project from 'src/api/project';
import FormatUtils from 'src/utils/formatters';
import { BfDatepicker } from 'src/components/Butterfly';
const MULTI_SURVEY_INTEGRATIONS = ['qualtrics'];
export default Vue.extend({
    components: { Modal, SurveyList, ObjectBrowser, Progressbar, BfDatepicker },
    data() {
        return {
            dataFile: null,
            integrationsDict: {},
            isLoading: false,
            isImporting: false,
            numResponses: null,
            provider: null,
            step: 'select-provider',
            surveyId: '',
            surveyName: null,
            objectPath: '',
            objectSize: null,
            importError: false,
            importErrorDetails: null,
            exportEndTime: null,
            exportStartTime: null,
        };
    },
    computed: {
        ...mapGetters(['dataUnits',])
    },
    mounted() {
        this.loadIntegrations();
    },
    methods: {
        number: FormatUtils.number,
        capitalize: FormatUtils.capitalize,
        startCase: FormatUtils.startCase,
        // Go to previous step, returns true unless we are already at the first step.
        goBack() {
            switch (this.step) {
                case 'confirm':
                    this.importError = false;
                    // Skip 2nd step for single survey providers
                    if (MULTI_SURVEY_INTEGRATIONS.includes(this.provider)) {
                        this.step = 'select-survey';
                    }
                    else {
                        this.step = 'select-provider';
                    }
                    return true;
                case 'select-survey':
                    this.step = 'select-provider';
                    return true;
                case 'select-s3-object':
                    this.step = 'select-provider';
                    return true;
                case 'select-gcs-object':
                    this.step = 'select-provider';
                    return true;
                case 'select-provider':
                    return false;
            }
        },
        isProviderAvailable(name) {
            return name in this.integrationsDict && this.integrationsDict[name].enabled;
        },
        loadIntegrations() {
            Project.getAllIntegrations()
                .then((response) => {
                // pack the list into a dict indexed by type
                this.integrationsDict = {};
                for (let i of response.results) {
                    this.integrationsDict[i.type] = i;
                    if (!i.enabled) {
                        this.$nextTick(() => {
                            new Tooltip(this.$el.querySelector(`.provider.${i.type}`), {
                                title: '<p>Integration disabled by your admin.</p>',
                                html: true
                            });
                        });
                    }
                }
            });
        },
        // Wait for a survey import to be fully loaded before execute the `callbackFn`
        pollImportStatus(callbackFn, errorFn) {
            // Check if we have a schema, if not, set a timeout to recall this function
            if (['PROCESSING', 'AWAITING', 'UPLOADED'].includes(this.dataFile.status)) {
                Project.fetchFileStatus(this.dataFile.id)
                    .then((response) => {
                    this.dataFile = response;
                });
                this.dataFileTimeout = setTimeout(this.pollImportStatus.bind(this, callbackFn, errorFn), 2000);
            }
            else if (this.dataFile.schema !== null || this.dataFile.status === 'INDEXING') {
                clearTimeout(this.dataFileTimeout);
                this.dataFileTimeout = null;
                // Get processed file samples
                Project.getFileFromBlobStore(this.dataFile.id)
                    .then((data) => {
                    let processedFile = data;
                    this.numResponses = processedFile.num_responses;
                    this.isImporting = false;
                    if (callbackFn) {
                        callbackFn(this.dataFile, {
                            headers: processedFile.headers,
                            samples: processedFile.samples
                        });
                    }
                });
                this.$analytics.track.integration.exportData(true, this.provider, this.numResponses || this.ObjectSize, this.dataFile.schema.length);
            }
            else if (this.dataFile.status === 'ERROR') {
                this.importError = true;
                this.importErrorDetails = this.dataFile.status_text;
                this.isImporting = false;
                if (errorFn) {
                    errorFn();
                }
            }
        },
        // Initiate import of survey responses.
        // Runs `callbackFn` when complete with the `dataFile` a metadata dict containing:
        // samples -- subset of responses
        // headers -- survey column headers
        runImport(callbackFn, errorFn) {
            this.importError = false;
            this.importErrorDetails = null;
            let params = { 'prev_export_cutoff': this.exportEndTime };
            if (this.provider == 'survey_monkey') {
                Object.assign(params, { 'surveyId': this.surveyId });
            }
            else if (this.provider == 'qualtrics') {
                Object.assign(params, {
                    'surveyId': this.surveyId,
                    'export_start_time': this.exportStartTime ? dayjs(this.exportStartTime).toISOString() : null,
                });
            }
            else if (this.provider === 's3') {
                Object.assign(params, { 's3ObjectPath': this.objectPath });
            }
            else if (this.provider === 'gcs') {
                Object.assign(params, { 'objectPath': this.objectPath });
            }
            this.isImporting = true;
            return Project.createIntegrationExport(this.provider, params)
                .then((response) => response.json())
                .then((data) => {
                this.dataFile = data;
                // Initiate status check
                this.pollImportStatus(callbackFn, errorFn);
            });
        },
        // Select a specific survey to load for a provider
        selectIntegrationSurvey(surveyId, surveyName, numResponses) {
            this.numResponses = numResponses;
            if (numResponses > this.dataUnits) {
                this.$refs.dataUnitsModal.show();
                return;
            }
            this.surveyId = surveyId;
            this.surveyName = surveyName;
            this.exportEndTime = dayjs().utc().toISOString();
            this.$emit('survey-selected', {
                'survey_id': this.surveyId,
                'num_responses': this.numResponses,
                'prev_export_cutoff': this.exportEndTime,
            });
            this.step = 'confirm';
        },
        selectFile(path, size) {
            this.objectSize = filesize(size);
            // Strip off the "bucket" prefix
            this.objectPath = path.split('/').slice(1).join('/');
            // Strip off the file name
            const folderPath = path.split('/').slice(0, -1).join('/');
            this.$emit('folder-selected', folderPath, this.provider);
            setTimeout(() => { this.step = 'confirm'; }, 500);
        },
        selectProvider(name) {
            this.importError = false;
            this.importErrorDetails = null;
            if (this.isProviderAvailable(name) === false) {
                return;
            }
            this.provider = name;
            const payload = {
                ...this.integrationsDict[this.provider]
            };
            this.$emit('provider-selected', payload);
            if (name === 'qualtrics') {
                this.surveyId = '';
                this.surveyName = null;
                this.step = 'select-survey';
            }
            else if (name === 'delighted') {
                this.surveyId = '';
                this.surveyName = null;
                this.isLoading = true;
                Project.getSurveyMetadata(name, '')
                    .then((response) => {
                    this.numResponses = response.result.response_count;
                    if (this.numResponses > this.dataUnits) {
                        this.$refs.dataUnitsModal.show();
                        return;
                    }
                    this.step = 'confirm';
                })
                    .finally(() => {
                    this.isLoading = false;
                });
            }
            else if (name === 's3') {
                // Fetch the list of objects from the bucket.
                this.objectPath = '';
                this.numResponses = null;
                this.objectSize = null;
                this.step = 'select-s3-object';
            }
            else if (name === 'gcs') {
                // Fetch the list of objects from the bucket.
                this.objectPath = '';
                this.numResponses = null;
                this.objectSize = null;
                this.step = 'select-gcs-object';
            }
            else if (name === 'survey_monkey') {
                this.surveyName = '';
                this.numResponses = null;
                this.step = 'select-survey';
            }
            else {
                throw new Error(`Unknown integration: ${name}`);
            }
        }
    }
});
